// IndexedDB schema for offline support.
// Only reads/writes structured data here — binary blobs live in
// blobCache.ts (Cache API). See plan for the full architecture.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Song, Collection } from '../../types/songbook'
import { OFFLINE_DB_NAME, OFFLINE_DB_VERSION } from '../../config/offline'

export interface CachedSongRecord {
  id: string
  song: Song
  cachedAt: number
  source: 'own' | 'shared'
  shareToken?: string | null
  // True when this song was created offline and has not yet been
  // pushed to Supabase (INSERT pending). Cleared by the sync queue
  // after a successful insert.
  pendingSync?: boolean
}

export interface CachedCollectionRecord {
  id: string
  collection: Collection
  cachedAt: number
  // Same semantics as CachedSongRecord.pendingSync.
  pendingSync?: boolean
}

export interface AttachmentPrefsRecord {
  songId: string
  includeAudio: boolean
  updatedAt: number
}

export interface MetaRecord {
  key: string
  value: unknown
}

interface OfflineDB extends DBSchema {
  songs: {
    key: string
    value: CachedSongRecord
  }
  collections: {
    key: string
    value: CachedCollectionRecord
  }
  attachmentPrefs: {
    key: string
    value: AttachmentPrefsRecord
  }
  meta: {
    key: string
    value: MetaRecord
  }
}

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null

// Returns a singleton DB handle. Safe to call multiple times —
// the browser will only open the DB once.
export function getOfflineDB(): Promise<IDBPDatabase<OfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(OFFLINE_DB_NAME, OFFLINE_DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('songs', { keyPath: 'id' })
          db.createObjectStore('collections', { keyPath: 'id' })
          db.createObjectStore('attachmentPrefs', { keyPath: 'songId' })
          db.createObjectStore('meta', { keyPath: 'key' })
        }
        // Future migrations: if (oldVersion < 2) { ... } (Phase 7 bumps to 2)
      },
      blocked() {
        console.warn('[offline] IDB upgrade blocked by another tab')
      },
      terminated() {
        console.warn('[offline] IDB connection terminated, will reopen on next access')
        dbPromise = null
      },
    }).catch((err) => {
      console.error('[offline] Failed to open IDB:', err)
      dbPromise = null
      throw err
    })
  }
  return dbPromise
}

// Resets the module-level cache. Used by tests and by the error
// recovery path. Does not delete the actual database.
export function _resetOfflineDB(): void {
  dbPromise = null
}
